/*
 * argile-stores.js — Points de vente.
 *
 * Recherche (ville / code postal, accent-insensible — le NOM n'est pas cherché
 * aujourd'hui, cf. `matchSearch`) + bascule Liste/Carte + modal de filtres
 * (facettes Type & Pays, multi-sélection façon collections/all) + tri + bouton
 * « Voir la suite » (load more client-side : `pageSize`, puis +`pageSize`).
 *
 * Tout est rendu dans le DOM ; le JS bascule la visibilité (classe `is-hidden`)
 * selon filtres + recherche + limite courante, et réordonne les cartes au tri.
 * Aucun appel réseau (un métaobjet n'est pas une collection paginable).
 */
(() => {
  const norm = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const init = (toolbar) => {
    const root = toolbar.closest('.argile-stores') || document;
    const grid = root.querySelector('[data-stores-grid]');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('[data-store-card]'));
    const searchInput = toolbar.querySelector('[data-stores-search]');
    const emptyEl = root.querySelector('[data-stores-empty]');
    const loadMore = root.querySelector('[data-stores-loadmore]');
    const moreBtn = root.querySelector('[data-stores-more]');
    const shownEl = root.querySelector('[data-stores-shown]');
    const totalEl = root.querySelector('[data-stores-total]');

    // Modal filtres
    const drawer = root.querySelector('[data-stores-drawer]');
    const openBtn = toolbar.querySelector('[data-stores-open-filters]');
    const closeBtns = root.querySelectorAll('[data-stores-close-filters]');
    const clearBtn = root.querySelector('[data-stores-clear]');
    const facetClearBtns = drawer
      ? Array.from(drawer.querySelectorAll('[data-stores-facet-clear]'))
      : [];
    const applyBtn = root.querySelector('[data-stores-apply]');
    const facetInputs = drawer
      ? Array.from(drawer.querySelectorAll('[data-stores-facet]'))
      : [];
    const facetCountEls = root.querySelectorAll('[data-stores-facet-count]');
    const bubbles = root.querySelectorAll('[data-stores-filter-count]');
    const resultsCountEl = root.querySelector('[data-stores-results-count]');

    // Tri
    const sortWrap = toolbar.querySelector('[data-stores-sort]');
    const sortBtns = sortWrap
      ? Array.from(sortWrap.querySelectorAll('[data-sort]'))
      : [];

    // Bascule Liste / Carte
    const viewToggle = root.querySelector('[data-stores-view-toggle]');
    const viewLabel = viewToggle && viewToggle.querySelector('[data-stores-view-label]');
    const panels = Array.from(root.querySelectorAll('[data-stores-panel]'));

    // — La barre d'outils et la bascule CHANGENT D'HÔTE selon la vue
    //
    // Un seul exemplaire de chaque dans le DOM, déplacé : en Carte la barre habite le
    // panneau flottant et la bascule se pose sur la carte ; en Liste, la barre remonte
    // au-dessus de la grille et la bascule se range dans sa seconde ligne — il n'y a
    // plus de carte sur laquelle se poser.
    //
    // 🔴 NE PAS DUPLIQUER POUR ÉVITER CE DÉPLACEMENT. Deux barres, ce sont deux champs
    // de recherche, deux compteurs de facettes et deux états de tri : la divergence ne
    // se voit qu'en basculant de vue au milieu d'un filtrage, donc jamais en recette.
    // Les écouteurs, eux, survivent au déplacement — ils sont posés sur les nœuds.
    const barre = root.querySelector('[data-stores-bar]');
    const hote = (cle) => root.querySelector('[data-stores-host="' + cle + '"]');
    const replacer = (vueCible) => {
      // LA BARRE D'ABORD : en vue Liste, l'hôte de la bascule est une ligne DE la barre.
      // Déplacer la bascule avant aurait déplacé un nœud vers un parent qui s'apprête
      // lui-même à bouger.
      const hoteBarre = hote(vueCible + '-barre');
      if (barre && hoteBarre && barre.parentNode !== hoteBarre) hoteBarre.appendChild(barre);
      // `prepend` et non `append` : en vue Liste, la bascule se range À GAUCHE
      // d'« affiner ma recherche » (retour du 11/09). Sur la carte elle est seule dans
      // son hôte, les deux reviennent au même.
      const hoteBascule = hote(vueCible + '-bascule');
      if (viewToggle && hoteBascule && viewToggle.parentNode !== hoteBascule) {
        hoteBascule.prepend(viewToggle);
      }
    };

    const pageSize = parseInt(toolbar.getAttribute('data-page-size'), 10) || 24;

    let query = '';
    let sort = 'default';
    let visible = pageSize;

    // — Recherche géographique
    const notice = root.querySelector('[data-stores-notice]');
    // Le texte vit dans un enfant depuis que le bandeau porte une croix : écrire dans
    // le conteneur effacerait le bouton.
    const noticeTexte = root.querySelector('[data-stores-notice-text]');
    const sortDistanceBtn = root.querySelector('[data-stores-sort-distance]');
    const RAYON_KM = 100;
    const REPLI_MAX = 5;

    /** Origine de la recherche par rayon, ou null quand aucune n'est active. */
    let origine = null;
    /** Points retenus par le rayon (Set de `meta`), ou null hors recherche géographique. */
    let retenus = null;
    /** Distances en km, indexées par entrée de `meta`. */
    const kmParPoint = new Map();

    /** Le module de calcul n'est chargé qu'à la première recherche géographique. */
    let geoPromesse = null;
    const chargerGeo = () => {
      if (!geoPromesse) geoPromesse = import(root.dataset.geoUrl);
      return geoPromesse;
    };

    /**
     * Remplit un gabarit traduit rendu par Liquid (`à %km% km` → `à 12 km`).
     * Marqueurs en `%x%` et non `{x}` : des accolades dans une chaîne Liquid font
     * croire à l'analyseur que la balise se termine, et le fichier est rejeté au push.
     */
    const message = (cle, valeurs = {}) => {
      const gabarit = notice ? notice.getAttribute('data-msg-' + cle) : '';
      return Object.entries(valeurs).reduce(
        (s, [k, v]) => s.split('%' + k + '%').join(v),
        gabarit || ''
      );
    };

    const annoncer = (texte) => {
      if (!notice) return;
      // On DÉMASQUE avant d'écrire : une région `aria-live` doit être dans l'arbre
      // d'accessibilité au moment où son contenu change, sinon rien n'est annoncé.
      notice.hidden = !texte;
      if (noticeTexte) noticeTexte.textContent = texte || '';
    };

    /**
     * Arrondi d'affichage : une décimale sous 10 km, l'entier au-delà. Un revendeur à
     * 300 m ne doit pas s'afficher « 0 km », et « 392,4 km » n'apporte rien.
     */
    const formatKm = (km) => {
      const langue = (document.documentElement.lang || 'fr').slice(0, 2);
      const valeur = km < 10 ? Math.round(km * 10) / 10 : Math.round(km);
      return valeur.toLocaleString(langue);
    };

    // Map carte → données (l'ordre initial sert de tri « Par défaut »).
    const meta = cards.map((card, i) => ({
      card,
      order: i,
      id: card.getAttribute('data-store-id') || String(i),
      type: card.getAttribute('data-type') || '',
      pays: card.getAttribute('data-pays') || '',
      nom: card.getAttribute('data-nom') || '',
      // Le nom sert au TRI sous sa forme brute et à la RECHERCHE sous forme normalisée :
      // « ATELIER DÉCORATION » doit se trouver en tapant « decoration ».
      nomNorm: norm(card.getAttribute('data-nom')),
      ville: norm(card.getAttribute('data-ville')),
      cp: norm(card.getAttribute('data-cp')),
      // NaN quand le point n'est pas géocodé — le module de calcul l'écarte alors,
      // au lieu de le traiter comme (0, 0) et de le remonter en tête.
      lat: parseFloat(card.getAttribute('data-lat')),
      lng: parseFloat(card.getAttribute('data-lng')),
      distance: card.querySelector('[data-store-distance]'),
    }));

    const activeValues = (facet) =>
      facetInputs
        .filter((i) => i.getAttribute('data-stores-facet') === facet && i.checked)
        .map((i) => i.value);

    // Une carte passe-t-elle les facettes (en ignorant éventuellement l'une d'elles) ?
    const matchFacets = (m, ignore) => {
      for (const f of ['type', 'pays']) {
        if (f === ignore) continue;
        const vals = activeValues(f);
        if (vals.length && !vals.includes(m[f])) return false;
      }
      return true;
    };

    // Recherche par localité : la ville ou le code postal doit COMMENCER par la
    // requête (préfixe). Évite les faux positifs quand le mot est au milieu du
    // nom de commune (ex. « paris » ne doit pas matcher « LE TOUQUET PARIS PLAGE »).
    const matchSearch = (m) =>
      !query || m.ville.startsWith(query) || m.cp.startsWith(query);

    // Le rayon est un prédicat de plus, pas un chemin parallèle : il se combine aux
    // facettes comme le reste. Hors recherche géographique, `retenus` vaut null et il
    // ne filtre rien.
    const matchRayon = (m) => !retenus || retenus.has(m);

    // — Colonne de résultats de la vue Carte
    //
    // PROJECTION des mêmes données que la grille, comme les pastilles : jamais un second
    // jeu de cartes à tenir synchronisé. Reconstruite uniquement quand l'onglet Carte est
    // affiché — rebâtir 120 lignes à chaque frappe ne servirait à rien.
    const aside = root.querySelector('[data-stores-aside]');
    // La Carte est la vue par défaut : elle répond directement à la question du
    // visiteur — « où, près de chez moi ? ». Contrepartie assumée : Mapbox est chargé
    // à chaque visite, le chargement paresseux ne joue plus que pour les visiteurs qui
    // n'atteignent jamais la section.
    let vue = 'carte';

    const signaler = (type, id) =>
      root.dispatchEvent(
        new CustomEvent('argile:stores:' + type, { bubbles: true, detail: { id, root } })
      );

    const rendreColonne = (liste) => {
      if (!aside) return;
      aside.textContent = '';

      if (!liste.length) {
        const vide = document.createElement('p');
        vide.className = 'argile-stores__row-empty';
        vide.textContent = aside.dataset.labelVide || '';
        aside.appendChild(vide);
        return;
      }

      liste.forEach((m) => {
        const ligne = document.createElement('article');
        ligne.className = 'argile-stores__row';
        ligne.dataset.storeId = m.id;

        // FICHE COMPLETE dans la colonne (retour Camille du 03/09) : type, nom, adresse,
        // distance, telephone, e-mail, reservation — les memes informations que la carte
        // de la vue Liste, dont on CLONE le corps. Projection et non recopie : c'est la
        // regle deja suivie pour les pastilles, elle interdit aux deux vues de diverger
        // et permet de reutiliser le CSS des cartes tel quel.
        // Les distances sont ecrites sur les cartes source JUSTE AVANT cet appel : le
        // clone les emporte deja remplies, rien a recalculer ici.
        const corps = m.card.querySelector('.argile-stores__body');
        if (corps) ligne.appendChild(corps.cloneNode(true));

        const resa = m.card.querySelector('.argile-stores__booking');
        if (resa) ligne.appendChild(resa.cloneNode(true));

        // La fiche entiere est cliquable a la souris, mais il faut UNE cible focusable
        // au clavier — et un conteneur qui porte des liens ne peut pas etre lui-meme un
        // bouton (contenu interactif imbrique). Le nom devient donc ce bouton.
        const nom = ligne.querySelector('.argile-stores__name');
        if (nom) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'argile-stores__row-btn';
          btn.textContent = nom.textContent.trim();
          nom.textContent = '';
          nom.appendChild(btn);
        }

        // Survol ET focus : la synchronisation doit exister au clavier aussi.
        // `focusin`/`focusout` et non `focus`/`blur` : ceux-la ne remontent pas, or la
        // cible focusable est desormais IMBRIQUEE dans la fiche.
        ligne.addEventListener('mouseenter', () => signaler('highlight', m.id));
        ligne.addEventListener('mouseleave', () => signaler('highlight', null));
        ligne.addEventListener('focusin', () => signaler('highlight', m.id));
        ligne.addEventListener('focusout', () => signaler('highlight', null));
        // Un clic sur un lien (telephone, e-mail, reservation) reste un clic sur ce
        // lien : sans ce garde-fou, appeler une boutique deplacerait aussi la carte.
        ligne.addEventListener('click', (e) => {
          if (e.target.closest('a')) return;
          signaler('focus', m.id);
        });

        aside.appendChild(ligne);
      });
    };

    // Clic sur une pastille : la colonne suit — c'est la moitié manquante de la
    // synchronisation, sans elle la carte est un cul-de-sac.
    document.addEventListener('argile:stores:pin', (e) => {
      if (!aside || !e.detail || e.detail.root !== root) return;
      aside
        .querySelectorAll('.argile-stores__row.is-active')
        .forEach((r) => r.classList.remove('is-active'));
      const ligne = aside.querySelector('[data-store-id="' + e.detail.id + '"]');
      if (!ligne) return;
      ligne.classList.add('is-active');
      ligne.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    const applySort = () => {
      const sorted = [...meta];
      const byText = (key, dir) =>
        sorted.sort((a, b) => a[key].localeCompare(b[key], 'fr') * dir);
      if (sort === 'nom-asc') byText('nom', 1);
      else if (sort === 'nom-desc') byText('nom', -1);
      else if (sort === 'ville-asc') byText('ville', 1);
      else if (sort === 'distance') {
        // Les points sans distance connue (non géocodés, ou hors recherche) partent
        // en fin de liste plutôt qu'en tête à 0 km.
        const km = (m) => (kmParPoint.has(m) ? kmParPoint.get(m) : Infinity);
        sorted.sort((a, b) => km(a) - km(b));
      } else sorted.sort((a, b) => a.order - b.order);
      // Réordonne le DOM (la grille suit l'ordre des nœuds).
      sorted.forEach((m) => grid.appendChild(m.card));
      if (emptyEl) grid.appendChild(emptyEl); // garde le message en fin de grille
    };

    const updateFacetCounts = () => {
      facetCountEls.forEach((el) => {
        const facet = el.getAttribute('data-stores-facet-count');
        const value = el.getAttribute('data-value');
        const n = meta.filter(
          (m) =>
            m[facet] === value && matchFacets(m, facet) && matchSearch(m) && matchRayon(m)
        ).length;
        el.textContent = '(' + n + ')';
        const input = facetInputs.find(
          (i) => i.getAttribute('data-stores-facet') === facet && i.value === value
        );
        if (input && !input.checked) input.disabled = n === 0;
      });
    };

    const updateBubbles = () => {
      const n = facetInputs.filter((i) => i.checked).length;
      bubbles.forEach((b) => {
        b.textContent = n;
        b.hidden = n === 0;
      });
    };

    const render = () => {
      // Liste dans l'ordre DOM courant (reflète le tri appliqué).
      const list = meta
        .filter((m) => m.card.parentNode === grid)
        .sort(
          (a, b) =>
            Array.prototype.indexOf.call(grid.children, a.card) -
            Array.prototype.indexOf.call(grid.children, b.card)
        );
      const visibleOrder = list.filter(
        (m) => matchFacets(m, null) && matchSearch(m) && matchRayon(m)
      );

      let shown = 0;
      meta.forEach((m) => m.card.classList.add('is-hidden'));
      visibleOrder.forEach((m, i) => {
        if (i < visible) {
          m.card.classList.remove('is-hidden');
          shown += 1;
        }
      });

      // `is-hidden` sert à DEUX choses : ne pas correspondre aux filtres, et être
      // au-delà de la limite du « Voir la suite ». La carte, elle, doit montrer tous
      // les points qui correspondent, y compris ceux que la liste n'a pas encore
      // déroulés — d'où ce drapeau distinct.
      meta.forEach((m) => m.card.setAttribute('data-match', '0'));
      visibleOrder.forEach((m) => m.card.setAttribute('data-match', '1'));

      // Distances : affichées uniquement pendant une recherche géographique.
      meta.forEach((m) => {
        if (!m.distance) return;
        const km = kmParPoint.get(m);
        const afficher = Boolean(origine) && km != null;
        m.distance.hidden = !afficher;
        if (afficher) m.distance.textContent = message('distance', { km: formatKm(km) });
      });

      const count = visibleOrder.length;
      if (emptyEl) emptyEl.hidden = count !== 0;
      if (resultsCountEl) resultsCountEl.textContent = count;

      if (loadMore) {
        const hasMore = count > visible;
        loadMore.hidden = count <= pageSize;
        if (moreBtn) moreBtn.style.display = hasMore ? '' : 'none';
        if (shownEl) shownEl.textContent = shown;
        if (totalEl) totalEl.textContent = count;
      }

      if (vue === 'carte') rendreColonne(visibleOrder);

      updateFacetCounts();
      updateBubbles();

      // La carte se reconstruit depuis le DOM : elle a besoin de savoir que le jeu
      // de résultats a changé, sans que ce fichier ait à la connaître.
      root.dispatchEvent(
        new CustomEvent('argile:stores:render', {
          bubbles: true,
          // `origine` dit à la carte s'il faut RECADRER : on ne le fait qu'après une
          // recherche géographique. Sinon le moindre filtre ferait sauter la carte d'un
          // bout de l'Europe à l'autre.
          detail: { root, origine: Boolean(origine) },
        })
      );
    };

    // — Bascule Liste / Carte
    //
    // Les deux vues rendent le MÊME jeu de résultats : on ne commute que la visibilité
    // des panneaux, jamais l'état de filtrage. Le changement est diffusé en événement
    // plutôt qu'appelé directement, pour que la carte — chargée paresseusement — s'y
    // branche sans que ce fichier ait à la connaître.
    /**
     * Décoche toutes les facettes, et dit si quelque chose l'était.
     *
     * NE REND PAS : ses deux appelants enchaînent déjà un rendu, et en déclencher un
     * second ferait clignoter la liste pour rien.
     */
    const effacerFacettes = () => {
      const actives = facetInputs.some((i) => i.checked);
      facetInputs.forEach((i) => {
        i.checked = false;
        i.disabled = false;
      });
      if (actives) visible = pageSize;
      return actives;
    };

    const setView = (next) => {
      vue = next;
      replacer(next);
      // 🔴 « AUTOUR DE MOI » NE SURVIT PAS AU CHANGEMENT DE VUE (retour du 11/09). Cette
      // recherche-là ne laisse RIEN dans le champ : en vue Liste, rien n'expliquerait
      // une liste réduite à quatre adresses, et le bouton qui l'a produite n'y est pas.
      // ⚠️ Une recherche par VILLE, elle, survit délibérément : son texte reste dans le
      // champ, donc elle s'explique et se défait toute seule. La perdre en basculant
      // pour lire les fiches serait pire — les deux vues doivent montrer le MÊME jeu de
      // résultats.
      if (origine && origine.geoloc) effacerOrigine();
      // 🔴 ET LES FACETTES NE PASSENT PAS EN VUE CARTE (retour du 11/09), pour la même
      // raison : « affiner ma recherche » n'y existe pas. Un filtre actif y masquerait
      // des points sans que rien à l'écran ne le dise ni ne permette de le lever.
      // ⚠️ Dans ce sens SEULEMENT : en vue Liste le contrôle est là, l'état est donc
      // visible et réversible — l'effacer en arrivant serait gratuit et déroutant.
      if (next === 'carte') effacerFacettes();
      // Le bouton nomme la vue de DESTINATION, jamais la vue courante : en Carte il
      // propose « Liste ». Son libellé EST l'action, il n'y a donc pas d'état à
      // exposer — pas d'`aria-pressed`, qui ferait croire à un interrupteur enfoncé.
      if (viewLabel && viewToggle) {
        viewLabel.textContent =
          next === 'carte' ? viewToggle.dataset.labelListe : viewToggle.dataset.labelCarte;
      }
      panels.forEach((p) => {
        p.hidden = p.getAttribute('data-stores-panel') !== next;
      });
      root.dispatchEvent(
        new CustomEvent('argile:stores:view', {
          bubbles: true,
          detail: { view: next, root },
        })
      );
      // La colonne n'est construite que sur l'onglet Carte : au basculement, il faut
      // donc la remplir.
      render();
    };

    // Plus de navigation aux fleches : elle appartenait au motif ARIA « tabs », qui
    // supposait plusieurs onglets. Un bouton unique se contente de son clic et de la
    // touche Entree, gratuitement.
    if (viewToggle) {
      viewToggle.addEventListener('click', () =>
        setView(vue === 'carte' ? 'liste' : 'carte')
      );
    }

    const effacerOrigine = () => {
      origine = null;
      retenus = null;
      kmParPoint.clear();
      if (sortDistanceBtn) sortDistanceBtn.hidden = true;
      if (sort === 'distance') {
        sort = 'default';
        sortBtns.forEach((b) =>
          b.setAttribute(
            'aria-checked',
            b.getAttribute('data-sort') === 'default' ? 'true' : 'false'
          )
        );
        applySort();
      }
      annoncer('');
    };

    /**
     * Biais de proximité du géocodage, selon le marché servi.
     *
     * Sans lui, « bord » remonte Bordj Bou Arreridj et « diot » Dioté Sayane : Mapbox
     * classe à l'échelle mondiale, alors que les revendeurs sont en France et treize
     * pays voisins. On ne RESTREINT pas les pays — un visiteur peut chercher Genève ou
     * Milan — on ordonne simplement par pertinence géographique.
     */
    const proximite = () =>
      (document.documentElement.lang || 'fr').slice(0, 2) === 'en'
        ? '-1.5,53.0' // Royaume-Uni, marché du /en
        : '2.35,46.6'; // France

    /** Géocodage TEMPORAIRE de la saisie : on ne stocke rien, donc palier gratuit. */
    const geocoder = async (texte) => {
      const jeton = root.dataset.mapboxToken;
      if (!jeton) return null;
      const u = new URL('https://api.mapbox.com/search/geocode/v6/forward');
      u.searchParams.set('q', texte);
      u.searchParams.set('access_token', jeton);
      u.searchParams.set('types', 'place,postcode');
      u.searchParams.set('limit', '1');
      u.searchParams.set('language', (document.documentElement.lang || 'fr').slice(0, 2));
      u.searchParams.set('proximity', proximite());
      const reponse = await fetch(u);
      if (!reponse.ok) return null;
      const json = await reponse.json();
      const trouve = json.features && json.features[0];
      if (!trouve) return null;
      const [lng, lat] = trouve.geometry.coordinates;
      return { lat, lng, nom: (trouve.properties && trouve.properties.name) || texte };
    };

    /** Pose une origine déjà connue (choisie dans les suggestions) et applique le rayon. */
    const appliquerLieu = async (lieu) => {
      const geo = await chargerGeo();
      const { resultats, repli } = geo.selection(lieu, meta, {
        rayonKm: RAYON_KM,
        repliMax: REPLI_MAX,
      });

      origine = lieu;
      retenus = new Set(resultats.map((r) => r.point));
      kmParPoint.clear();
      resultats.forEach((r) => kmParPoint.set(r.point, r.km));

      // Le filtre texte est SUSPENDU : « Paris » a servi à trouver un lieu, il ne doit
      // pas en plus écarter les communes alentour dont le nom ne commence pas par là.
      query = '';

      if (sortDistanceBtn) sortDistanceBtn.hidden = false;
      sort = 'distance';
      sortBtns.forEach((b) =>
        b.setAttribute(
          'aria-checked',
          b.getAttribute('data-sort') === 'distance' ? 'true' : 'false'
        )
      );

      // Le suffixe `-un` porte la forme singulière, rendue par Liquid via `count:` —
      // sinon on annonce « 1 points de vente ». Les deux formes sont traduites dans
      // `locales/`, le JS ne fait que choisir.
      const cle = (repli ? 'repli' : 'rayon') + (resultats.length === 1 ? '-un' : '');
      annoncer(
        message(cle, { rayon: RAYON_KM, lieu: lieu.nom, n: resultats.length })
      );

      applySort();
      visible = pageSize;
      render();
    };

    /** Valider sans rien choisir : on géocode la saisie telle quelle. */
    const rechercherAutour = async (texte) => {
      let lieu = null;
      try {
        lieu = await geocoder(texte);
      } catch (e) {
        console.error('[argile-stores] géocodage :', e);
      }
      if (!lieu) return annoncer(message('introuvable'));
      return appliquerLieu(lieu);
    };

    // — Suggestions : une seule liste, DEUX sections
    //
    // *Lieux* vient du géocodage distant, *Points de vente* d'une correspondance locale.
    // Les fondre en une liste résout la collision entre l'autocomplétion et le filtre
    // texte, qui visaient tous deux la frappe — et rend VISIBLE le repli qui était
    // implicite : retrouver une enseigne par son nom, ou un point mal géocodé.
    const suggEl = root.querySelector('[data-stores-suggestions]');
    const DELAI_FRAPPE = 300;
    let suggOptions = [];
    let suggActif = -1;
    let suggTimer = null;
    let suggAbort = null;
    let suggSeq = 0;

    const fermerSuggestions = () => {
      if (!suggEl) return;
      suggEl.hidden = true;
      suggEl.textContent = '';
      suggOptions = [];
      suggActif = -1;
      searchInput.setAttribute('aria-expanded', 'false');
      searchInput.removeAttribute('aria-activedescendant');
    };

    const activerSuggestion = (i) => {
      suggOptions.forEach((o, n) => {
        o.el.setAttribute('aria-selected', n === i ? 'true' : 'false');
        o.el.classList.toggle('is-active', n === i);
      });
      suggActif = i;
      if (i < 0) return searchInput.removeAttribute('aria-activedescendant');
      searchInput.setAttribute('aria-activedescendant', suggOptions[i].el.id);
      suggOptions[i].el.scrollIntoView({ block: 'nearest' });
    };

    /** Correspondance locale : `includes` sur le NOM, `startsWith` sur ville et code
     *  postal. La divergence est voulue — le faux positif qu'évite le préfixe sur les
     *  villes (« paris » ne doit pas remonter « Le Touquet Paris Plage ») n'a pas
     *  d'équivalent sur les enseignes, et sans `includes` « COLLECTION par DIOT PGM »
     *  reste introuvable en tapant « diot ». */
    const pointsLocaux = (q) =>
      meta
        .filter((m) => m.nomNorm.includes(q) || m.ville.startsWith(q) || m.cp.startsWith(q))
        .slice(0, 5);

    /** @returns liste de lieux, ou null si le service est indisponible, ou undefined
     *  si la requête a été annulée par une frappe plus récente. */
    const lieuxDistants = async (texte) => {
      const jeton = root.dataset.mapboxToken;
      if (!jeton) return null;
      // Annule la requête en vol : sans ça, une réponse lente arrivée après une réponse
      // rapide réécrit la liste avec des résultats périmés — un défaut intermittent,
      // donc difficile à reproduire et facile à livrer.
      if (suggAbort) suggAbort.abort();
      suggAbort = new AbortController();
      const u = new URL('https://api.mapbox.com/search/geocode/v6/forward');
      u.searchParams.set('q', texte);
      u.searchParams.set('access_token', jeton);
      u.searchParams.set('types', 'place,postcode');
      u.searchParams.set('limit', '5');
      u.searchParams.set('language', (document.documentElement.lang || 'fr').slice(0, 2));
      u.searchParams.set('proximity', proximite());
      try {
        const r = await fetch(u, { signal: suggAbort.signal });
        if (!r.ok) return null;
        const j = await r.json();
        return (j.features || []).map((f) => ({
          nom: (f.properties && f.properties.name) || texte,
          contexte: (f.properties && f.properties.place_formatted) || '',
          lat: f.geometry.coordinates[1],
          lng: f.geometry.coordinates[0],
        }));
      } catch (e) {
        if (e.name === 'AbortError') return undefined;
        console.error('[argile-stores] suggestions :', e);
        return null;
      }
    };

    const rendreSuggestions = (lieux, points) => {
      if (!suggEl) return;
      suggEl.textContent = '';
      suggOptions = [];
      suggActif = -1;

      const ajouterGroupe = (titre, items, remplir) => {
        if (!items.length) return;
        const groupe = document.createElement('div');
        groupe.className = 'argile-stores__sugg-group';
        groupe.setAttribute('role', 'group');
        groupe.setAttribute('aria-label', titre);
        const entete = document.createElement('p');
        entete.className = 'argile-stores__sugg-title';
        entete.textContent = titre;
        groupe.appendChild(entete);
        items.forEach((item) => {
          const opt = document.createElement('button');
          opt.type = 'button';
          opt.className = 'argile-stores__sugg';
          opt.setAttribute('role', 'option');
          opt.setAttribute('aria-selected', 'false');
          opt.id = suggEl.id + '-opt-' + suggOptions.length;
          remplir(opt, item);
          suggOptions.push({ el: opt, item });
          groupe.appendChild(opt);
        });
        suggEl.appendChild(groupe);
      };

      ajouterGroupe(suggEl.dataset.labelLieux, lieux || [], (opt, lieu) => {
        opt.textContent = lieu.contexte ? lieu.nom + ' — ' + lieu.contexte : lieu.nom;
        opt.dataset.kind = 'lieu';
        opt.addEventListener('click', () => choisirLieu(lieu));
      });

      ajouterGroupe(suggEl.dataset.labelPoints, points, (opt, m) => {
        const nom = m.card.querySelector('.argile-stores__name');
        const loc = m.card.querySelector('[data-store-locality]');
        opt.textContent =
          (nom ? nom.textContent.trim() : m.nom) + (loc ? ' — ' + loc.textContent.trim() : '');
        opt.dataset.kind = 'point';
        opt.addEventListener('click', () => choisirPoint(m));
      });

      // Le service de lieux est tombé, mais la recherche locale reste utile : on le dit
      // sans vider la liste.
      if (lieux === null) {
        const avis = document.createElement('p');
        avis.className = 'argile-stores__sugg-note';
        avis.textContent = suggEl.dataset.labelHs;
        suggEl.appendChild(avis);
      }
      if (!suggOptions.length && lieux !== null) {
        const vide = document.createElement('p');
        vide.className = 'argile-stores__sugg-note';
        vide.textContent = suggEl.dataset.labelVide;
        suggEl.appendChild(vide);
      }

      suggEl.hidden = false;
      searchInput.setAttribute('aria-expanded', 'true');
    };

    const proposer = (texte) => {
      const q = norm(texte);
      if (!q) return fermerSuggestions();
      const seq = ++suggSeq;
      const points = pointsLocaux(q);
      // La section locale s'affiche IMMÉDIATEMENT : elle ne dépend d'aucun réseau.
      rendreSuggestions([], points);
      lieuxDistants(texte).then((lieux) => {
        if (lieux === undefined || seq !== suggSeq) return; // frappe plus récente
        rendreSuggestions(lieux, points);
      });
    };

    const choisirLieu = (lieu) => {
      fermerSuggestions();
      appliquerLieu(lieu);
    };

    const choisirPoint = (m) => {
      fermerSuggestions();
      effacerOrigine();
      query = '';
      // Mener DIRECTEMENT au point choisi : on ne garde que lui, puis on l'amène à
      // l'écran. Vider le champ rétablit la liste complète.
      retenus = new Set([m]);
      visible = pageSize;
      render();
      m.card.scrollIntoView({ block: 'center', behavior: 'smooth' });
    };

    const boutonEffacerChamp = root.querySelector('[data-stores-search-clear]');
    /** La croix du champ n'existe que quand il y a quelque chose à effacer. */
    const majBoutonEffacer = () => {
      if (boutonEffacerChamp && searchInput) {
        boutonEffacerChamp.hidden = !searchInput.value;
      }
    };

    /**
     * Remise à zéro de la recherche — la MÊME pour les deux croix (retour du 11/09).
     *
     * Elle lève tout ce qu'une recherche a pu poser : le texte du champ, le filtre
     * texte, l'origine géographique, le rayon, le tri par distance et le bandeau. Une
     * croix qui ne lèverait qu'une moitié laisserait une liste restreinte sans rien à
     * l'écran pour l'expliquer — exactement le défaut qu'elles corrigent.
     */
    const reinitialiserRecherche = () => {
      if (searchInput) searchInput.value = '';
      query = '';
      fermerSuggestions();
      effacerOrigine();
      majBoutonEffacer();
      visible = pageSize;
      render();
      if (searchInput) searchInput.focus();
    };

    if (boutonEffacerChamp) {
      boutonEffacerChamp.addEventListener('click', reinitialiserRecherche);
    }
    const boutonEffacerBandeau = root.querySelector('[data-stores-notice-clear]');
    if (boutonEffacerBandeau) {
      boutonEffacerBandeau.addEventListener('click', reinitialiserRecherche);
    }

    // — Recherche
    if (searchInput) {
      // Au fil de la frappe : filtre texte local, instantané et gratuit. Toute frappe
      // annule la recherche géographique en cours — sinon les deux se combineraient et
      // le rayon exclurait des communes voisines au motif que leur nom diffère.
      searchInput.addEventListener('input', (e) => {
        // Sans condition : un géocodage échoué ne pose PAS d'origine, mais laisse un
        // bandeau « Lieu introuvable » qui resterait affiché sur des résultats valides.
        effacerOrigine();
        query = norm(e.target.value);
        majBoutonEffacer();
        visible = pageSize;
        render();

        // Suggestions temporisées : sans ça, chaque frappe part au géocodage et une
        // seule recherche consomme dix requêtes.
        clearTimeout(suggTimer);
        const texte = e.target.value.trim();
        if (!texte) return fermerSuggestions();
        suggTimer = setTimeout(() => proposer(texte), DELAI_FRAPPE);
      });

      searchInput.addEventListener('keydown', (e) => {
        const ouvert = suggEl && !suggEl.hidden && suggOptions.length;

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          if (!ouvert) return;
          e.preventDefault();
          const pas = e.key === 'ArrowDown' ? 1 : -1;
          const n = suggOptions.length;
          activerSuggestion((suggActif + pas + n) % n);
          return;
        }

        if (e.key === 'Escape') {
          if (ouvert) e.preventDefault();
          return fermerSuggestions();
        }

        if (e.key !== 'Enter') return;
        e.preventDefault();
        clearTimeout(suggTimer);
        // Une suggestion est surlignée : c'est elle qu'on valide. Sinon, on géocode la
        // saisie telle quelle.
        if (ouvert && suggActif >= 0) return suggOptions[suggActif].el.click();
        fermerSuggestions();
        const texte = searchInput.value.trim();
        if (texte) rechercherAutour(texte);
      });

      // Fermeture au clic extérieur, comme le menu de tri.
      document.addEventListener('click', (e) => {
        if (suggEl && !suggEl.hidden && !suggEl.contains(e.target) && e.target !== searchInput) {
          fermerSuggestions();
        }
      });
    }

    // — « Autour de moi »
    const nearBtn = toolbar.querySelector('[data-stores-near]');
    if (nearBtn) {
      // Révélé seulement si l'API existe ET que le contexte est sécurisé : hors HTTPS
      // les navigateurs refusent la géolocalisation sans rien dire de clair. Un bouton
      // présent mais inopérant est pire qu'un bouton absent.
      if (navigator.geolocation && window.isSecureContext) nearBtn.hidden = false;

      const nearLabel = nearBtn.querySelector('[data-stores-near-label]');
      let localisationEnCours = false;

      nearBtn.addEventListener('click', () => {
        // Obtenir une position peut prendre plusieurs secondes ; sans état d'attente le
        // bouton paraît cassé, et un second clic lancerait une seconde demande.
        if (localisationEnCours) return;
        localisationEnCours = true;
        nearBtn.setAttribute('aria-busy', 'true');
        if (nearLabel) nearLabel.textContent = nearBtn.dataset.labelLoading;

        const terminer = () => {
          localisationEnCours = false;
          nearBtn.removeAttribute('aria-busy');
          if (nearLabel) nearLabel.textContent = nearBtn.dataset.labelIdle;
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            terminer();
            fermerSuggestions();
            searchInput.value = '';
            query = '';
            appliquerLieu({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              nom: nearBtn.dataset.labelPosition,
              // Marque l'origine comme venant de la GÉOLOCALISATION : elle ne laisse
              // aucune trace dans le champ, et ne survit donc pas au changement de vue
              // (voir `setView`).
              geoloc: true,
            });
          },
          (erreur) => {
            terminer();
            // Trois messages distincts : « refusé » et « indisponible » appellent des
            // gestes différents du visiteur, les confondre le laisse sans solution.
            const cle =
              erreur.code === 1 ? 'msgDenied' : erreur.code === 3 ? 'msgTimeout' : 'msgUnavailable';
            annoncer(nearBtn.dataset[cle]);
          },
          // La page reste dans l'état où elle était pendant l'attente : on ne vide rien
          // avant d'avoir une position.
          { timeout: 10000, maximumAge: 300000 }
        );
      });
    }

    // — Modal : ouverture / fermeture
    if (drawer && openBtn) {
      openBtn.addEventListener('click', () => {
        if (typeof drawer.showModal === 'function') drawer.showModal();
        else drawer.setAttribute('open', '');
      });
    }
    const closeDrawer = () => {
      if (drawer && drawer.open && typeof drawer.close === 'function') {
        drawer.close();
      } else if (drawer) {
        drawer.removeAttribute('open');
      }
    };
    closeBtns.forEach((b) => b.addEventListener('click', closeDrawer));
    if (applyBtn) applyBtn.addEventListener('click', closeDrawer);
    // Clic sur l'overlay sombre (::backdrop) = ferme le tiroir, comme le
    // catalogue. Le panneau n'occupe que la colonne droite : un clic sur le
    // fond a `target === drawer` (l'élément <dialog> lui-même), un clic dans
    // le contenu cible un enfant → on ne ferme que dans le 1er cas.
    if (drawer) {
      drawer.addEventListener('click', (e) => {
        if (e.target === drawer) closeDrawer();
      });
    }

    // — Facettes (live)
    facetInputs.forEach((input) => {
      input.addEventListener('change', () => {
        visible = pageSize;
        render();
      });
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        effacerFacettes();
        visible = pageSize;
        render();
      });
    }

    // « Effacer » par facette (Type / Pays)
    facetClearBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const facet = btn.getAttribute('data-stores-facet-clear');
        facetInputs
          .filter((i) => i.getAttribute('data-stores-facet') === facet)
          .forEach((i) => {
            i.checked = false;
            i.disabled = false;
          });
        visible = pageSize;
        render();
      });
    });

    // — Tri
    sortBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        sort = btn.getAttribute('data-sort');
        sortBtns.forEach((b) =>
          b.setAttribute('aria-checked', b === btn ? 'true' : 'false')
        );
        applySort();
        visible = pageSize;
        render();
        if (sortWrap) sortWrap.removeAttribute('open');
      });
    });
    // Ferme le menu tri au clic extérieur.
    document.addEventListener('click', (e) => {
      if (sortWrap && sortWrap.open && !sortWrap.contains(e.target)) {
        sortWrap.removeAttribute('open');
      }
    });

    // — Load more
    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        visible += pageSize;
        render();
      });
    }

    render();
  };

  const boot = () => document.querySelectorAll('[data-stores]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
